
import React from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw } from 'lucide-react';
import { generateBio } from '@/utils/bioGenerator';
import { useToast } from "@/hooks/use-toast";

interface BioSectionProps {
  bioValue: string;
  professionValue: string;
  skillsValue: string[];
  languagesValue: string[];
  locationValue: string;
  educationValue: string;
  experienceLevel: string;
  getYearsFromExperienceLevel: (level: string) => number;
  isGeneratingBio: boolean;
  setBioValue: (value: string) => void;
  setIsGeneratingBio: (value: boolean) => void;
}

const BioSection: React.FC<BioSectionProps> = ({
  bioValue,
  professionValue,
  skillsValue,
  languagesValue,
  locationValue,
  educationValue,
  experienceLevel,
  getYearsFromExperienceLevel,
  isGeneratingBio,
  setBioValue,
  setIsGeneratingBio
}) => {
  const { toast } = useToast();

  const handleGenerateBio = async () => {
    setIsGeneratingBio(true);
    try {
      const generatedBio = await generateBio({
        name: null,
        profession: professionValue,
        skills: skillsValue,
        experience: experienceLevel ? getYearsFromExperienceLevel(experienceLevel) : 0,
        education: educationValue,
        languages: languagesValue,
        location: locationValue
      });

      setBioValue(generatedBio);
    } catch (error) {
      console.error('Error generating bio:', error);
      toast({
        title: "Error generating bio",
        description: "There was a problem generating your bio. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingBio(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium block">Bio</label>
        <Button
          variant="outline"
          size="sm"
          onClick={handleGenerateBio}
          disabled={isGeneratingBio || !professionValue}
          className="flex items-center gap-2"
        >
          {isGeneratingBio ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          Generate Bio
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Fill in your profile details to generate a personalized bio automatically
      </p>
      <Textarea
        placeholder="Write a short bio about yourself..."
        className="h-32"
        value={bioValue}
        onChange={(e) => setBioValue(e.target.value)}
      />
    </div>
  );
};

export default BioSection;
